'use client';

import {
  useCommendaOSEntitySchema,
  useCommendaOSSchema,
} from '@commenda/api/commenda-os-dsl';
import {
  Badge,
  Label,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { useEffect, useMemo } from 'react';
import { COMMENDA_OS_DATA_SOURCE } from '../../constants';
import { SchemaFieldPicker } from '../../editor/SchemaFieldPicker';
import type { CommendaOSFieldConfig, CommendaOSFilter, DataSourceConfig, FormDSL } from '@commenda/types';
import { ContextBindingEditor } from './ContextBindingEditor';
import {
  getContextBindingEditorKeys,
  getContextSourceFields,
} from './context-helpers';

// Location type selector for keyPerson locations field (used inside AutoDetectFieldEditor)
function LocationTypeSelector({
  entitySchema,
  commendaOS,
  updateCommendaOS,
}: {
  entitySchema: unknown;
  commendaOS: CommendaOSFieldConfig;
  updateCommendaOS: (updates: Partial<CommendaOSFieldConfig>) => void;
}) {
  const locationsField = entitySchema as {
    fields?: Record<
      string,
      {
        filters?: Record<
          string,
          { enumOptions?: Array<{ value: string; label: string }> }
        >;
      }
    >;
  } | null;
  const locationTypeFilterSchema =
    locationsField?.fields?.locations?.filters?.locationType;
  const locationTypeOptions = locationTypeFilterSchema?.enumOptions ?? [
    { value: 'MAILING_ADDRESS', label: 'Mailing Address' },
    {
      value: 'PRINCIPAL_PLACE_OF_BUSINESS',
      label: 'Headquarters / Operating Address',
    },
  ];
  const selectedLocationTypeRaw = commendaOS?.filters?.find(
    (f: CommendaOSFilter) => f.key === 'locationType'
  )?.value;
  const selectedLocationType =
    selectedLocationTypeRaw != null && selectedLocationTypeRaw !== ''
      ? String(selectedLocationTypeRaw)
      : '';
  return (
    <div className="space-y-2">
      <Label>Location Type</Label>
      <Select
        value={selectedLocationType || undefined}
        onValueChange={(value) => {
          const rest = (commendaOS?.filters ?? []).filter(
            (f: CommendaOSFilter) => f.key !== 'locationType'
          );
          updateCommendaOS({
            filters: value ? [...rest, { key: 'locationType', value }] : rest,
          });
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select location type..." />
        </SelectTrigger>
        <SelectContent>
          {locationTypeOptions.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="txt-xs-regular text-text-tertiary">
        Which address to collect for this key person (e.g. Mailing, Operating).
      </p>
    </div>
  );
}

export function AutoDetectFieldEditor({
  dataSource,
  onChange,
  currentFieldId,
  dsl,
  onUpdateLabel,
  onUpdateProps,
}: {
  dataSource?: DataSourceConfig;
  onChange: (dataSource: DataSourceConfig | undefined) => void;
  currentFieldId?: string;
  dsl?: FormDSL;
  onUpdateLabel?: (label: string) => void;
  onUpdateProps?: (key: string, value: unknown) => void;
}) {
  const { entities } = useCommendaOSSchema();

  useEffect(() => {
    if (!dataSource || dataSource.type !== COMMENDA_OS_DATA_SOURCE) {
      onChange({
        type: COMMENDA_OS_DATA_SOURCE,
        commendaOS: {
          entity: 'corporation',
          field: { read: '' },
          mode: 'read-write',
          contextBindings: [],
        },
      });
    }
  }, [dataSource, onChange]);

  const commendaOS = dataSource?.commendaOS;
  const entity = commendaOS?.entity || 'corporation';

  const entityOptions = useMemo(() => {
    const wanted = [
      'corporation',
      'company',
      'keyPerson',
      'keyPersonRelationship',
      'transferPricingPolicy',
      'transferPricingBenchmark',
    ];
    const list = wanted
      .map((key) => {
        const ent = entities?.[key] as { label?: string } | undefined;
        return ent
          ? { value: key, label: ent.label || key }
          : null;
      })
      .filter((entry): entry is { value: string; label: string } => entry !== null);
    if (list.length > 0) return list;
    return wanted.map((key) => ({ value: key, label: key }));
  }, [entities]);

  const { entitySchema } = useCommendaOSEntitySchema(entity);

  // Whether the selected target field has create/update paths in the live schema.
  const schemaCanWrite = useMemo(() => {
    if (!entitySchema || !commendaOS?.field?.read) return false;

    const targetPath = commendaOS.field.read;

    const findFieldWritability = (
      fields: Record<string, unknown>,
      parentPath: string = ''
    ): boolean =>
      Object.entries(fields).some(([key, field]) => {
        const f = field as Record<string, unknown>;
        const readConfig = f.read as { path?: string } | undefined;
        const currentPath = parentPath ? `${parentPath}.${key}` : key;

        const hasUpdate = !!(f.update as { path?: string })?.path;
        const hasCreate = !!(f.create as { path?: string })?.path;

        if (currentPath === targetPath) return hasUpdate || hasCreate;
        if (readConfig?.path === targetPath) return hasUpdate || hasCreate;

        if (f.type === 'object' && f.fields) {
          return findFieldWritability(
            f.fields as Record<string, unknown>,
            currentPath
          );
        }
        return false;
      });

    return findFieldWritability(entitySchema.fields as Record<string, unknown>);
  }, [entitySchema, commendaOS?.field?.read]);

  const updateCommendaOS = (updates: Partial<CommendaOSFieldConfig>) => {
    onChange({
      type: COMMENDA_OS_DATA_SOURCE,
      commendaOS: {
        entity: commendaOS?.entity || 'corporation',
        field: commendaOS?.field || { read: '' },
        mode: commendaOS?.mode || 'read-write',
        contextBindings: commendaOS?.contextBindings,
        filters: commendaOS?.filters,
        ...updates,
      },
    });
  };

  const directorDocumentKindOptions = useMemo(() => {
    const fromSchema = (
      entitySchema as {
        filters?: {
          documentKind?: {
            enumOptions?: Array<{ value: string; label: string }>;
          };
        };
      }
    )?.filters?.documentKind?.enumOptions;
    if (fromSchema?.length) return fromSchema;
    return [
      { value: 'governmentId', label: 'Government ID' },
      { value: 'panCard', label: 'Pan Card' },
      { value: 'aadharCard', label: 'Aadhar Card' },
      { value: 'addressProof', label: 'Address Proof' },
      { value: 'dscProof', label: 'DSC Proof' },
      { value: 'bankTransferProof', label: 'Bank Transfer Proof' },
    ];
  }, [entitySchema]);

  const documentKindFilter = commendaOS?.filters?.find(
    (f: CommendaOSFilter) => f.key === 'documentKind'
  );
  const selectedDocumentKind = documentKindFilter?.value ?? '';

  const contextSourceFields = useMemo(() => {
    if (!dsl || !entity) return [];
    return getContextSourceFields(dsl, currentFieldId, entity);
  }, [dsl, currentFieldId, entity]);

  const formContextKeys = useMemo(() => {
    const requirements = (
      dsl as unknown as {
        form?: { contextRequirements?: { required?: string[] } };
      }
    )?.form?.contextRequirements;
    return requirements?.required || [];
  }, [dsl]);

  if (!commendaOS) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <LoadingIndicator size={16} />
          <span className="txt-small-regular text-text-tertiary">
            Initializing...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h3 className="txt-small-medium text-text-secondary">
          Field Configuration
        </h3>
        <Badge variant="gray">Commenda OS</Badge>
      </div>

      <div className="space-y-4 pl-4 border-l-2 border-border-secondary">
        {/* Entity Selection */}
        <div className="space-y-2">
          <Label>Entity</Label>
          <Select
            value={entity}
            onValueChange={(value) => {
              updateCommendaOS({
                entity: value,
                field: { read: '' },
                contextBindings: [],
                filters: [],
              });
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {entityOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Director Document: document kind filter (required for directorDocument entity) */}
        {entity === 'directorDocument' && (
          <div className="space-y-2">
            <Label>Document kind</Label>
            <Select
              value={String(selectedDocumentKind) || undefined}
              onValueChange={(value) => {
                updateCommendaOS({
                  filters: value ? [{ key: 'documentKind', value }] : [],
                });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select document kind..." />
              </SelectTrigger>
              <SelectContent>
                {directorDocumentKindOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="txt-xs-regular text-text-tertiary">
              Required for director document fields (e.g. Aadhar Card,
              Government ID).
            </p>
          </div>
        )}

        {/* Field Selection */}
        <div className="space-y-2">
          <Label>Field</Label>
          <SchemaFieldPicker
            entity={entity}
            value={commendaOS.field.read}
            onSelect={(path, label, fieldTypeInfo) => {
              const canWrite =
                !!fieldTypeInfo?.updatePath || !!fieldTypeInfo?.createPath;

              updateCommendaOS({
                field: {
                  read: path,
                  update: fieldTypeInfo?.updatePath,
                  create: fieldTypeInfo?.createPath,
                },
                mode: canWrite ? 'read-write' : 'read',
              });
              if (onUpdateLabel && path) {
                onUpdateLabel(label || path);
              }
              if (onUpdateProps && fieldTypeInfo) {
                const resolvedType =
                  fieldTypeInfo.parsingType || fieldTypeInfo.type;
                onUpdateProps('resolvedFieldType', resolvedType);
              }
            }}
            placeholder="Select a field..."
          />
        </div>

        {/* Location Type - when keyPerson locations field is selected (show right after Field so it's visible) */}
        {entity === 'keyPerson' &&
          String(commendaOS?.field?.read ?? '')
            .trim()
            .toLowerCase() === 'locations' && (
            <LocationTypeSelector
              entitySchema={entitySchema}
              commendaOS={commendaOS}
              updateCommendaOS={updateCommendaOS}
            />
          )}

        {/* Mode Selection - Uses live schema to determine available modes */}
        <div className="space-y-2">
          <Label>Mode</Label>
          <Select
            value={commendaOS.mode}
            onValueChange={(value) =>
              updateCommendaOS({
                mode: value as 'read' | 'read-write',
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="read">Read Only</SelectItem>
              {schemaCanWrite && (
                <SelectItem value="read-write">Read & Write</SelectItem>
              )}
            </SelectContent>
          </Select>
          {schemaCanWrite ? (
            <p className="txt-xs-regular text-text-tertiary">
              Read Only: Display value but don&apos;t allow editing. Read &
              Write: Display and save changes.
            </p>
          ) : (
            <p className="txt-xs-regular text-text-tertiary">
              This field is read-only and cannot be updated after creation.
            </p>
          )}
        </div>

        {/* Context Source - Always show for AutoDetectField */}
        <ContextBindingEditor
          entity={entity}
          contextKeys={getContextBindingEditorKeys(entitySchema, entity)}
          contextBindings={commendaOS.contextBindings}
          availableFields={contextSourceFields}
          formContextKeys={formContextKeys}
          onChange={(bindings) =>
            updateCommendaOS({ contextBindings: bindings })
          }
        />
      </div>
    </div>
  );
}
